import { useId, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { MAX_CLIENT_EMAILS } from '../../../models/vacation.schemas';
import {
    CLIENT_EMAIL_HINT,
    CLIENT_EMAIL_LIMIT_MESSAGE,
    CLIENT_EMAIL_NEXT_PLACEHOLDER,
    CLIENT_EMAIL_PLACEHOLDER,
} from '../vacationRequestForm.definitions';

export interface ClientEmailsInputProps {
    emails: string[];
    onAdd: (email: string) => void;
    onRemove: (index: number) => void;
    disabled?: boolean;
    error?: string;
}

export function ClientEmailsInput({
    emails,
    onAdd,
    onRemove,
    disabled = false,
    error,
}: ClientEmailsInputProps) {
    const inputId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [draft, setDraft] = useState('');
    const [draftError, setDraftError] = useState('');

    const canAdd = emails.length < MAX_CLIENT_EMAILS;
    const isAtLimit = emails.length >= MAX_CLIENT_EMAILS;

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleAddEmail = (email: string) => {
        const trimmed = email.trim();
        if (!trimmed) {
            setDraftError('');
            return;
        }

        if (!validateEmail(trimmed)) {
            setDraftError('E-mail do cliente inválido');
            return;
        }

        if (emails.includes(trimmed)) {
            setDraftError('Os e-mails de cliente não podem se repetir');
            return;
        }

        if (emails.length >= MAX_CLIENT_EMAILS) {
            return;
        }

        onAdd(trimmed);
        setDraft('');
        setDraftError('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
            e.preventDefault();
            handleAddEmail(draft);
        } else if (e.key === 'Backspace' && draft === '' && emails.length > 0) {
            e.preventDefault();
            const lastEmail = emails[emails.length - 1];
            onRemove(emails.length - 1);
            setDraft(lastEmail);
            setDraftError('');
        }
    };

    const handleBlur = () => {
        if (draft.trim() && !draftError) {
            handleAddEmail(draft);
        }
    };

    const handleRemoveEmail = (index: number) => {
        onRemove(index);
        setDraftError('');
        inputRef.current?.focus();
    };

    return (
        <div>
            <div className="relative rounded-xl border border-primary-200 bg-white p-2.5">
                <div className="flex flex-wrap gap-2">
                    {emails.map((email, index) => (
                        <div
                            key={`${email}-${index}`}
                            className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-sm text-primary-700 font-montserrat"
                        >
                            <span className="truncate">{email}</span>
                            <button
                                type="button"
                                onClick={() => handleRemoveEmail(index)}
                                disabled={disabled}
                                className="flex h-4 w-4 items-center justify-center text-primary-600 hover:text-primary-800 disabled:opacity-50"
                                aria-label={`Remover ${email}`}
                            >
                                <XMarkIcon className="h-3 w-3" />
                            </button>
                        </div>
                    ))}

                    {!isAtLimit && (
                        <input
                            ref={inputRef}
                            id={inputId}
                            type="email"
                            value={draft}
                            onChange={(e) => {
                                setDraft(e.target.value);
                                setDraftError('');
                            }}
                            onKeyDown={handleKeyDown}
                            onBlur={handleBlur}
                            disabled={disabled}
                            className="flex-1 min-w-[150px] border-0 bg-transparent p-0 text-sm font-montserrat focus:outline-none focus:ring-0"
                            placeholder={emails.length > 0 ? CLIENT_EMAIL_NEXT_PLACEHOLDER : CLIENT_EMAIL_PLACEHOLDER}
                            autoComplete="email"
                        />
                    )}

                    {isAtLimit && (
                        <span className="flex items-center text-xs text-raro-gray-500 font-montserrat">
                            {CLIENT_EMAIL_LIMIT_MESSAGE}
                        </span>
                    )}
                </div>
            </div>

            {draftError && (
                <p className="mt-1 text-xs text-red-600 font-montserrat">{draftError}</p>
            )}

            {error && !draftError && (
                <p className="mt-1 text-xs text-red-600 font-montserrat">{error}</p>
            )}

            <p className="mt-1 text-xs text-raro-gray-500 font-montserrat">{CLIENT_EMAIL_HINT}</p>
        </div>
    );
}

export default ClientEmailsInput;
